import {
  getFriendsSpotReserveName,
  type FriendsSpotSelectionEntry
} from './friendsSpotsReservePayload';
import { isFriendsSpotsUnavailable } from './reserveSpotContract';
import { trimStringOrEmpty } from './spotName';

export type ButtonActionMode = 'book' | 'skip' | 'disabled';

export type ButtonActionModeContext = {
  bookingId: string;
  selectedSpot: { spotName: string } | null;
  layoutError: unknown;
  skipLoadingAvailable: boolean;
  /**
   * Once every friends slot is refilled, {@link canBookSelectedSpot} wins and the mode returns to
   * `'book'` even while this error is still set — the host must clear it when selections change
   * (or on a successful re-pick) so a stale error banner does not show next to an enabled CTA.
   */
  bookSpotError: { message: string; message_code?: string } | null;
  hasBookableSpots: boolean | null;
  isInteractiveSpotBookingEnabled: boolean;
  isSkipInFlight: boolean;
  /**
   * Friends mode (BOOKING-4174): when true, booking requires every slot in
   * {@link friendsSpotSelections} to be filled; {@link selectedSpot} is ignored (slot 0 is the
   * member's spot and the host's selectedSpot state is not updated in friends mode).
   * Omit / false = legacy single-spot behavior, unchanged.
   */
  isSpotSelectionForFriendsEnabled?: boolean;
  /** Friends mode only: latest selections array from onFriendsSpotSelectionChange. */
  friendsSpotSelections?: FriendsSpotSelectionEntry[];
  guestsCount?: number;
  /** Server assigned a spot mid-flow — CTA is Skip Spot Booking only. */
  isServerAssignedLocked?: boolean;
};

/**
 * True when every slot (member + friends) has a non-blank spot assigned. When
 * `expectedSlotCount` is provided, the selections array must also have exactly that many slots.
 */
export function areAllFriendsSpotsSelected(
  friendsSpotSelections: readonly FriendsSpotSelectionEntry[] | undefined,
  expectedSlotCount?: number
): boolean {
  if (friendsSpotSelections === undefined || friendsSpotSelections.length === 0) return false;
  if (expectedSlotCount !== undefined && friendsSpotSelections.length !== expectedSlotCount) {
    return false;
  }
  return friendsSpotSelections.every((selection) => getFriendsSpotReserveName(selection) !== null);
}

export function hasSpotSelection(
  selectedSpot: FriendsSpotSelectionEntry,
  friendsSpotSelections: readonly FriendsSpotSelectionEntry[]
): boolean {
  if (getFriendsSpotReserveName(selectedSpot) !== null) return true;
  return friendsSpotSelections.some((selection) => getFriendsSpotReserveName(selection) !== null);
}

/**
 * In friends mode ({@link ButtonActionModeContext.isSpotSelectionForFriendsEnabled}), a reserve
 * failure stamped with {@link FRIENDS_SPOTS_UNAVAILABLE_CODE} empties only the taken slots so the
 * member can re-pick — it is recoverable and does not make skip available. Any other
 * `bookSpotError` (500, network) is a dead end and still does. Genuine dead ends (layout error, no
 * bookable spots, slow layout) offer skip in every mode.
 */
export function isSkipOptionAvailable(
  context: Pick<
    ButtonActionModeContext,
    | 'layoutError'
    | 'skipLoadingAvailable'
    | 'bookSpotError'
    | 'hasBookableSpots'
    | 'isInteractiveSpotBookingEnabled'
    | 'isSpotSelectionForFriendsEnabled'
    | 'isServerAssignedLocked'
  >
): boolean {
  const {
    layoutError,
    skipLoadingAvailable,
    bookSpotError,
    hasBookableSpots,
    isInteractiveSpotBookingEnabled,
    isSpotSelectionForFriendsEnabled,
    isServerAssignedLocked
  } = context;

  const isRecoverableFriendsError =
    isSpotSelectionForFriendsEnabled === true && isFriendsSpotsUnavailable(bookSpotError);

  return (
    isServerAssignedLocked === true ||
    layoutError != null ||
    skipLoadingAvailable ||
    (bookSpotError != null && !isRecoverableFriendsError) ||
    (isInteractiveSpotBookingEnabled && hasBookableSpots === false)
  );
}

export function canBookSelectedSpot(context: ButtonActionModeContext): boolean {
  if (context.isServerAssignedLocked === true) return false;
  if (context.isSpotSelectionForFriendsEnabled === true) {
    const expectedSlotCount =
      context.guestsCount === undefined ? undefined : context.guestsCount + 1;
    if (!areAllFriendsSpotsSelected(context.friendsSpotSelections, expectedSlotCount)) {
      return false;
    }
    if (!context.bookingId) return false;
    return context.hasBookableSpots !== false;
  }
  if (!context.selectedSpot) return false;
  if (trimStringOrEmpty(context.selectedSpot.spotName) === '') return false;
  if (!context.bookingId) return false;
  return context.hasBookableSpots !== false;
}

export function getButtonActionMode(context: ButtonActionModeContext): ButtonActionMode {
  if (context.isSkipInFlight) return 'skip';
  if (context.isServerAssignedLocked === true) return 'skip';
  if (canBookSelectedSpot(context)) return 'book';
  if (isSkipOptionAvailable(context)) {
    return 'skip';
  }
  return 'disabled';
}
